/*----------------------------------
- SECTION: SOURCE COMPARISON TABLE
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { getAppraisalProvider } from '@uniquedomains/core/domains/appraisal';

import type {
    PRODUCT_APPRAISE_PAGE_CONTENT,
    ProductAppraiseSource,
} from '@/client/catalogs/landing/product/appraise/pageContent';

/*----------------------------------
- TYPES
----------------------------------*/

type SourceComparisonTableProps = {
    headers: (typeof PRODUCT_APPRAISE_PAGE_CONTENT)['comparison']['headers'];
    rows: readonly ProductAppraiseSource[];
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/**
 * One tree, two shapes: a five-column table from `lg` up, a stack of cards on a
 * phone. The row is the grid from `lg` up and its two inner groups become
 * `display: contents` there, so the cells land in the header's columns without a
 * second markup tree. `Status` is drawn first for the phone card, where it belongs
 * beside the name, and ordered last for the table.
 */
const GRID_CLASS = 'lg:grid lg:grid-cols-[1fr_1.2fr_1.4fr_1.2fr_0.6fr] lg:gap-4';
const CELL_CLASS = 'text-[12.5px] leading-[1.45] text-[#4a4d4e] lg:text-[14.5px] lg:leading-[1.5]';
const CELL_LABEL_CLASS = 'block text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#6a695e] lg:hidden';

/*----------------------------------
- COMPONENT
----------------------------------*/

/** What each source answers with and what it judges a name on. Live state is read
 *  from the provider catalog, never restated in copy. */
const SourceComparisonTable = ({ headers, rows }: SourceComparisonTableProps) => (
    /* `divide` rather than a border on each row: the separators are drawn by the table
       itself, so a row can drop every border it needs as a phone card. */
    <div
        data-reveal
        class="mt-[18px] flex flex-col gap-2 lg:mt-8 lg:gap-0 lg:divide-y lg:divide-[#f1ede6] lg:overflow-hidden lg:rounded-[24px] lg:border lg:border-[#ebe7de] lg:bg-white"
    >
        <div
            class={`hidden ${GRID_CLASS} px-6 py-4 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#6a695e]`}
        >
            <span>{headers.source}</span>
            <span>{headers.returns}</span>
            <span>{headers.judgesOn}</span>
            <span>{headers.bestFor}</span>
            <span>{headers.status}</span>
        </div>

        {rows.map((row) => {
            const provider = getAppraisalProvider(row.id);
            if (!provider) return null;

            const isLive = provider.status === 'live';

            return (
                <div
                    key={row.id}
                    class={`rounded-[18px] border border-[#ebe7de] bg-white px-4 py-3.5 ${GRID_CLASS} lg:items-center lg:rounded-none lg:border-0 lg:px-6 lg:py-[18px]`}
                >
                    <div class="flex items-center justify-between gap-2 lg:contents">
                        <span class="text-[15px] font-bold lg:text-[14.5px]">{provider.label}</span>
                        <span
                            class={`inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-bold lg:order-1 lg:text-[12.5px] ${isLive ? 'text-[#2d7a55]' : 'text-[#66696b]'}`}
                        >
                            <span
                                aria-hidden="true"
                                class={`h-[7px] w-[7px] rounded-full ${isLive ? 'bg-[#2d7a55]' : 'bg-[#c9c8c2]'}`}
                            />
                            {isLive ? 'Live' : 'Soon'}
                        </span>
                    </div>

                    <div class="mt-2.5 grid grid-cols-2 gap-2.5 lg:contents">
                        <span class={CELL_CLASS}>
                            <span class={CELL_LABEL_CLASS}>{headers.returns}</span>
                            {row.returns}
                        </span>
                        <span class={CELL_CLASS}>
                            <span class={CELL_LABEL_CLASS}>{headers.judgesOn}</span>
                            {row.judgesOn}
                        </span>
                        {/* The phone card keeps the two columns that separate the sources;
                            "Best for" is an editorial extra the narrow layout has no room for. */}
                        <span class={`hidden lg:block ${CELL_CLASS}`}>{row.bestFor}</span>
                    </div>
                </div>
            );
        })}
    </div>
);

export default SourceComparisonTable;
